import { PlusIcon } from '@phosphor-icons/react';
import { Formik } from 'components/form/formik';
import diff from 'object-diff';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { COUNTRY_VALUES } from '@utils/address';
import * as Yup from 'yup';

import { Button } from '../../../../components/button/Button';
import { InputField } from '../../../../components/input/InputField';
import { SimpleSelectField } from '../../../../components/select/SimpleSelectField';
import { TextAreaField } from '../../../../components/textarea/TextAreaField';
import { BillingCadence, Country, Language, SelfBillingMode } from 'src/types/enums';
import { updateSupplier, useSupplierBankAccount } from '../../models';
import { getDisplayError } from '../../../../utils/get-display-error';
import { LANGUAGE_VALUES } from '../../../../utils/language';
import { formatVatNumber } from '../../../../utils/vat-number';
import { decimateAndFormatNumber, parseNumberInput } from '../../../../utils/number';
import { useSupplier } from './supplierContext';
import { useMinimalGeneralSettings } from '../../../../contexts/minimal-settings-context';
import { AutocompletePostalcode } from '../../../location/components/AutocompletePostalcode';
import { VatRateComboboxField, vatRateToComboboxItem } from '../../../order/vatRate/VatRateComboboxField';
import { CheckboxField } from '../../../../components/checkbox/CheckboxField';
import { Card } from 'components/Card';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../../utils/track-event';
import { defaultPaymentTerm, paymentTerms, SelectPaymentTerm } from 'components/select/SelectPaymentTerm';
import { InvoiceChannelsField } from '../../../customer/pages/Customer/InvoiceChannelsField';
import { customerFieldsFromInvoiceChannels } from '../../../customer/document-delivery';
import { selfBillChannelsFromSupplier } from '../../self-bill-delivery';
import { SupplierBankAccountCard } from './SupplierBankAccountCard';
import { SELF_BILLING_MODE_VALUES, selfBillingModeItem } from '../../self-billing-mode';

const BILLING_CADENCE_VALUES = [
  { key: BillingCadence.WEEKLY, name: 'Wekelijks (maandag)' },
  { key: BillingCadence.DAILY, name: 'Dagelijks (spot)' },
];

const updateSupplierSchema = Yup.object().shape({
  accountingId: Yup.string(),
  name: Yup.string().min(1, 'Vereist').required('Vereist'),
  vatNumber: Yup.string().vatNumber().required('Vereist'),
  companyNumber: Yup.string().companyNumber().required('Vereist'),
  street: Yup.string().min(1, 'Vereist').required('Vereist'),
  streetNumber: Yup.string(),
  city: Yup.string().min(1, 'Vereist').required('Vereist'),
  postalCode: Yup.string().min(1, 'Vereist').required('Vereist'),
  country: Yup.mixed().nullable().required('Vereist'),
  language: Yup.mixed().nullable().required('Vereist'),
  paymentTerm: Yup.object()
    .shape({
      code: Yup.string().required(),
      value: Yup.string().required(),
      days: Yup.number().required(),
    })
    .required('Vereist'),
  selfBillingMode: Yup.mixed<{ key: SelfBillingMode }>().required('Vereist'),
  selfBillChannels: Yup.array(),
  regularCarrier: Yup.bool().required('Vereist'),
  billingCadence: Yup.mixed().nullable().required('Vereist'),
  disableCmrReminders: Yup.bool().required('Vereist'),
  dieselSurchargeEnabled: Yup.bool().required('Vereist'),
  hasCustomDieselSurcharge: Yup.bool().required('Vereist'),
  dieselSurchargePercentage: Yup.string().matches(/^-?[0-9]+(\.[0-9]+)?$/, 'Ongeldig getal'),
});

/**
 * Self billing can only be switched on with an approved IBAN in effect: a valid account and
 * no change waiting for review. The server enforces the same rule. A carrier that is already
 * self-billed keeps its mode, and the option stays hidden until the bank account is known.
 */
function useSelfBillingBlock(
  supplierId: number,
  currentMode: SelfBillingMode,
): { isBlocked: boolean; reason?: string } {
  const { data } = useSupplierBankAccount(supplierId);
  if (currentMode === SelfBillingMode.SelfBilling) {
    return { isBlocked: false };
  }
  if (!data) {
    return { isBlocked: true };
  }
  if (!data.ibanValid) {
    return { isBlocked: true, reason: 'Self billing kan pas aan met een goedgekeurde IBAN.' };
  }
  if (data.changes.some((change) => change.status === 'Pending')) {
    return { isBlocked: true, reason: 'Self billing kan pas aan als de IBAN-wijziging goedgekeurd is.' };
  }
  return { isBlocked: false };
}

export const UpdateSupplierForm = () => {
  const { supplier, refreshData } = useSupplier();
  const { settings } = useMinimalGeneralSettings();
  const selfBillingBlock = useSelfBillingBlock(supplier.id, supplier.selfBillingMode);
  const selfBillingModeItems = selfBillingBlock.isBlocked
    ? SELF_BILLING_MODE_VALUES.filter((item) => item.key !== SelfBillingMode.SelfBilling)
    : SELF_BILLING_MODE_VALUES;

  const initialValues = useMemo(() => {
    return {
      accountingId: supplier.accountingId ?? '' + supplier.id,
      name: supplier.name,
      vatNumber: formatVatNumber(supplier.vatNumber),
      companyNumber: supplier.companyNumber,
      street: supplier.street,
      streetNumber: supplier.streetNumber,
      city: supplier.city,
      postalCode: supplier.postalCode,
      internalNotes: supplier.internalNotes,
      country: COUNTRY_VALUES.find((v) => v.key === supplier.country)!,
      language: LANGUAGE_VALUES.find((v) => v.key === supplier.language)!,
      paymentTerm: paymentTerms.find((v) => v.code === supplier.paymentTerm) ?? defaultPaymentTerm,
      truckAmount: `${supplier.truckAmount}`,
      tautlinerAmount: `${supplier.tautlinerAmount}`,
      boxTrailerAmount: `${supplier.boxTrailerAmount}`,
      openTrailerAmount: `${supplier.openTrailerAmount}`,
      defaultVatRate: supplier.defaultVatRate ? vatRateToComboboxItem(supplier.defaultVatRate) : null,
      selfBillingMode: selfBillingModeItem(supplier.selfBillingMode),
      selfBillChannels: selfBillChannelsFromSupplier(supplier),
      regularCarrier: supplier.regularCarrier,
      billingCadence:
        BILLING_CADENCE_VALUES.find((v) => v.key === supplier.billingCadence) ?? BILLING_CADENCE_VALUES[0],
      disableCmrReminders: supplier.disableCmrReminders,
      dieselSurchargeEnabled: supplier.dieselSurchargeEnabled,
      hasCustomDieselSurcharge: supplier.hasCustomDieselSurcharge,
      dieselSurchargePercentage: decimateAndFormatNumber(supplier.dieselSurchargePercentage, 2),
    };
  }, [supplier]);

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={updateSupplierSchema}
        onSubmit={async (newValues) => {
          try {
            const patch: Partial<typeof newValues> = diff(initialValues, newValues);
            const defaultVatRateId = patch.defaultVatRate?.key;
            delete patch.defaultVatRate;
            const dieselSurchargePercentage =
              patch.dieselSurchargePercentage !== undefined
                ? patch.dieselSurchargePercentage
                  ? parseNumberInput(patch.dieselSurchargePercentage, 2)
                  : 0
                : undefined;
            delete patch.dieselSurchargePercentage;
            const channelFields =
              patch.selfBillChannels !== undefined ? customerFieldsFromInvoiceChannels(newValues.selfBillChannels) : {};
            delete patch.selfBillChannels;
            const dieselSurchargeEnabled = patch.dieselSurchargeEnabled;
            const hasCustomDieselSurcharge = patch.hasCustomDieselSurcharge;
            delete patch.dieselSurchargeEnabled;
            delete patch.hasCustomDieselSurcharge;
            const updateData: any = {
              ...patch,
              ...channelFields,
              country: patch.country ? (patch.country.key as Country) : undefined,
              paymentTerm: patch.paymentTerm,
              language: patch.language ? (patch.language.key as Language) : undefined,
              truckAmount: patch.truckAmount ? +patch.truckAmount : undefined,
              tautlinerAmount: patch.tautlinerAmount ? +patch.tautlinerAmount : undefined,
              boxTrailerAmount: patch.boxTrailerAmount ? +patch.boxTrailerAmount : undefined,
              openTrailerAmount: patch.openTrailerAmount ? +patch.openTrailerAmount : undefined,
              defaultVatRateId,
              dieselSurchargeEnabled,
              hasCustomDieselSurcharge,
              dieselSurchargePercentage,
            };
            if (!newValues.dieselSurchargeEnabled) {
              updateData.hasCustomDieselSurcharge = false;
              updateData.dieselSurchargePercentage = 0;
            }
            if (!newValues.hasCustomDieselSurcharge) {
              updateData.dieselSurchargePercentage = 0;
            }
            if (Object.values(updateData).filter((v) => v !== undefined).length) {
              await updateSupplier(supplier.id, {
                ...updateData,
                paymentTerm: updateData.paymentTerm?.code,
                billingCadence: updateData.billingCadence?.key,
                selfBillingMode: updateData.selfBillingMode?.key,
              });
              refreshData();
              trackEvent('supplier_updated', {
                supplier_id: supplier.id,
                fields_changed: Object.keys(updateData),
              });
            }
            toast.success('Vervoerder aangepast');
          } catch (err: any) {
            captureException(err);
            toast.error('Kon vervoerder niet aanpassen: ' + getDisplayError(err));
          }
        }}
      >
        {({ handleSubmit, isSubmitting, values, setFieldValue }) => {
          return (
            <form onSubmit={handleSubmit} className="grid gap-4">
              <Card title="Bedrijfsgegevens">
                <InputField labelText="Naam" type="text" name="name" />
                <InputField labelText="BTW Nummer" type="text" name="vatNumber" />
                <InputField labelText="Ondernemingsnummer" type="text" name="companyNumber" />
                <div className="flex gap-4">
                  <div className="flex-4">
                    <InputField labelText="Straat" type="text" name="street" />
                  </div>
                  <div className="flex-1">
                    <InputField labelText="Nr" type="text" name="streetNumber" />
                  </div>
                </div>
                <AutocompletePostalcode />
                <SimpleSelectField labelText="Taal" items={LANGUAGE_VALUES} name="language" />
              </Card>

              <Card title="Facturatie">
                <InputField labelText="ID Boekhouding" type="text" name="accountingId" />
                <SelectPaymentTerm
                  name="paymentTerm"
                  value={values.paymentTerm}
                  onChange={(value) => setFieldValue('paymentTerm', value)}
                />
                <VatRateComboboxField labelText="Standaard BTW Tarief" name="defaultVatRate" />
                <div className="grid grid-cols-2 gap-4">
                  <CheckboxField labelText="Vaste vervoerder" name="regularCarrier" />
                  <CheckboxField labelText="CMR herinneringen uitschakelen" name="disableCmrReminders" />
                </div>
                <SimpleSelectField
                  labelText="Ritoverzicht / self billing"
                  items={selfBillingModeItems}
                  name="selfBillingMode"
                  helperText={
                    values.selfBillingMode.key !== supplier.selfBillingMode && supplier.openSelfBillDraftCount > 0
                      ? 'Openstaande concepten blijven zoals ze zijn. Verwijder ze om opnieuw te genereren.'
                      : selfBillingBlock.reason
                  }
                />
                {values.selfBillingMode.key !== SelfBillingMode.None && (
                  <>
                    <SimpleSelectField labelText="Frequentie" items={BILLING_CADENCE_VALUES} name="billingCadence" />
                    <InvoiceChannelsField
                      name="selfBillChannels"
                      labelText="Verzenden via"
                      helperText="Altijd zichtbaar in het carrier portal. Peppol enkel bij self billing."
                    />
                  </>
                )}
              </Card>

              <SupplierBankAccountCard supplierId={supplier.id} country={supplier.country} />

              <Card title="Diesel toeslag">
                <CheckboxField labelText="Gebruikt diesel toeslag" name="dieselSurchargeEnabled" />
                {values['dieselSurchargeEnabled'] && (
                  <div>
                    <CheckboxField
                      labelText="Eigen diesel toeslag"
                      name="hasCustomDieselSurcharge"
                      isDisabled={!values['dieselSurchargeEnabled']}
                    />
                    <InputField
                      labelText="Eigen diesel toeslag"
                      type="text"
                      name="dieselSurchargePercentage"
                      step="0.01"
                      isDisabled={!values['dieselSurchargeEnabled'] || !values['hasCustomDieselSurcharge']}
                    />
                    <div className="text-sm">
                      {`Dieseltoeslag voor transporteur is ${decimateAndFormatNumber(
                        values.dieselSurchargeEnabled
                          ? values.hasCustomDieselSurcharge
                            ? values.dieselSurchargePercentage
                              ? parseNumberInput(values.dieselSurchargePercentage, 2)
                              : 0
                            : settings.dieselSurchargePercentage
                          : 0,
                        2,
                      )}%`}
                    </div>
                  </div>
                )}
              </Card>

              <Card title="Vloot">
                <div className="grid grid-cols-2 gap-4">
                  <InputField labelText="Aantal trekkers" type="number" name="truckAmount" />
                  <InputField labelText="Aantal tautliners" type="number" name="tautlinerAmount" />
                  <InputField labelText="Aantal box/frigo trailers" type="number" name="boxTrailerAmount" />
                  <InputField labelText="Aantal open trailers" type="number" name="openTrailerAmount" />
                </div>
              </Card>

              <Card title="Notities">
                <TextAreaField labelText="Interne notities" name="internalNotes" spellCheck={true} />
              </Card>

              <div>
                <Button
                  type="submit"
                  color="primary"
                  iconLeft={<PlusIcon className="button-icon" />}
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                >
                  Bewaar aanpassingen
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};
